import type { HTMLProps, MouseEvent } from 'react';

import { isExternalLink } from './isExternalLink';
import { redirectLink } from './redirectLink';

type PartialAnchorProperties = Pick<HTMLProps<HTMLAnchorElement>, 'href' | 'target' | 'rel' | 'onClick'>;

const SCHEME = /^\/?(mailto:|tel:)/i;
const LEADING_SLASH_SCHEME = /^\/(mailto:|tel:)/i;

/** True for `mailto:`/`tel:` hrefs, tolerating a stray leading slash (see `normalizeSchemeHref`). */
export function isSchemeLink(link: string): boolean {
  return SCHEME.test(link);
}

/**
 * `mailto:`/`tel:` hrefs sometimes arrive with a stray leading slash — cx-api footer data emits e.g.
 * `/mailto:marketing@oddschecker.com` — which a browser resolves as a relative page path instead of
 * handing off to the mail/phone client. Strip it so the scheme link behaves. Non-scheme links and
 * already-correct scheme links are returned unchanged.
 */
export function normalizeSchemeHref(link: string): string {
  return link.replace(LEADING_SLASH_SCHEME, '$1');
}

export function formatLinkProperties(link: string, isDesktop = true, callback?: () => void): PartialAnchorProperties {
  const isExternal = isExternalLink(link);
  const href = isExternal || isSchemeLink(link) ? normalizeSchemeHref(link) : cleanLink(`/${link}`);
  return {
    href,
    ...(isDesktop
      ? {}
      : {
          onClick: (e: MouseEvent<HTMLAnchorElement>) => redirectLink(e, href, callback),
        }),
    ...(isExternal ? { target: '_blank', rel: 'noopener' } : {}),
  };
}

function cleanLink(link: string): string {
  return link.replace(/\/\//, '/');
}
